import React, { useState, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOrgInstitution } from '../contexts/OrgInstitutionContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import './Students.css';
import { 
  Group as GroupIcon, 
  PersonAdd as PersonAddIcon, 
  CloudDownload as CloudDownloadIcon, 
  Search as SearchIcon, 
  Sort as SortIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  AutoAwesome as WizardIcon
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import Header from './Header';
import GoogleClassroomImport from './GoogleClassroomImport';
import NotificationModal from './NotificationModal';
import StudentOnboardingWizard from './StudentOnboardingWizard';
import DuplicateResolutionModal from './DuplicateResolutionModal';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  date_of_birth: string | null;
  age: number | null;
  reading_level: number | null;
  grade_level: string | null;
  primary_language: string | null;
  assignment_count: number;
  cultures: string[];
  email: string | null;
  student_id: string | null;
  is_active: boolean;
  created_at: string;
  last_activity_date: string | null;
  class_ids: number[];
  class_count: number;
  in_my_roster?: boolean;  // For institution view
  created_by_me?: boolean; // For institution view
}

interface Culture {
  id: number;
  code: string;
  name: string;
  description: string | null;
  manageable?: boolean;
  culture_type?: 'organization' | 'teacher';
  created_by_user_id?: number;
  created_by_user_name?: string;
}

interface FormData {
  first_name: string;
  last_name: string;
  reading_level: string;
  grade_level: string;
  student_id: string;
  email: string;
  parent_email: string;
  phone: string;
  password: string;
  primary_language: string;
  culture_ids: number[];
  notes: string;
}

const Students: React.FC = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { selectedOrganizationId, selectedInstitutionId } = useOrgInstitution();
  const { t } = useTranslation();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingStudent, setDeletingStudent] = useState<Student | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [permanentDeletingStudent, setPermanentDeletingStudent] = useState<Student | null>(null);
  const [isPermanentDeleting, setIsPermanentDeleting] = useState(false);
  const [isGoogleClassroomImportOpen, setIsGoogleClassroomImportOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStudent, setWizardStudent] = useState<Student | null>(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<any[]>([]);
  const [pendingStudentData, setPendingStudentData] = useState<any>(null);
  const [viewType, setViewType] = useState<'all' | 'my_classes' | 'institution'>('my_classes');
  
  // Real-time name search state for add form
  const [nameSearchResults, setNameSearchResults] = useState<(Student & { in_my_roster?: boolean })[]>([]);
  const [isSearchingNames, setIsSearchingNames] = useState(false);
  const [showNameMatches, setShowNameMatches] = useState(false);
  
  // Add to roster modal state
  const [isAddToRosterModalOpen, setIsAddToRosterModalOpen] = useState(false);
  const [studentToAddToRoster, setStudentToAddToRoster] = useState<Student | null>(null);
  const [teacherClasses, setTeacherClasses] = useState<{ id: number; name: string; subject?: string; student_count?: number }[]>([]);
  const [selectedClassesForRoster, setSelectedClassesForRoster] = useState<number[]>([]);
  const [isAddingToRoster, setIsAddingToRoster] = useState(false);
  
  const [notificationModal, setNotificationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({ isOpen: false, title: '', message: '', type: 'info' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Form state
  const [cultures, setCultures] = useState<Culture[]>([]);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    reading_level: '',
    grade_level: '',
    student_id: '',
    email: '',
    parent_email: '',
    phone: '',
    password: '',
    primary_language: '',
    culture_ids: [],
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Track total students in database vs filtered results
  const [totalStudentsInDatabase, setTotalStudentsInDatabase] = useState(0);
  
  // Simple stats for now
  const stats = {
    total_students: totalStudentsInDatabase,
    has_students: totalStudentsInDatabase > 0,
    recent_additions: 0,
    grade_distribution: [],
    reading_level_distribution: []
  };

  // Fetch students data with search and sort
  const fetchStudents = async (isInitialLoad = false) => {
    if (!token || !isAuthenticated) return;
    
    try {
      // Only set main loading for initial load, use dataLoading for searches/sorts
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setDataLoading(true);
      }
      
      let response;
      
      if (viewType === 'institution') {
        // Use institution students endpoint that includes roster status
        const queryParams = new URLSearchParams({
          search: searchTerm,
          page: page.toString(),
          per_page: '25'
        });
        response = await apiGet(`/api/students/institution-students?${queryParams}`, token);
      } else {
        // Use regular students endpoint
        const queryParams = new URLSearchParams({
          search: searchTerm,
          status: statusFilter,
          sort_by: sortBy,
          sort_order: sortOrder,
          page: page.toString(),
          per_page: '25',
          view_type: viewType
        });

        // Add additional filters from context (for super admins and org admins)
        if (selectedOrganizationId) {
          queryParams.append('organization_id', selectedOrganizationId.toString());
        }
        if (selectedInstitutionId) {
          queryParams.append('institution_id', selectedInstitutionId.toString());
        }

        response = await apiGet(`/api/students?${queryParams}`, token);
      }

      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
        setTotalPages(data.pagination ? data.pagination.pages : 1);
        
        // Track total students in database (only update if no search/filter applied)
        if (!searchTerm && sortBy === 'last_name' && sortOrder === 'asc' && page === 1 && viewType !== 'institution') {
          setTotalStudentsInDatabase(data.pagination ? data.pagination.total : (data.students || []).length);
        }
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudents([]); // Ensure we have an empty array
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setDataLoading(false);
      }
    }
  };

  // Fetch cultures for display (all cultures)
  const fetchCultures = async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await apiGet('/api/students/cultures', token);

      if (response.ok) {
        const data = await response.json();
        setCultures(data.cultures || []);
      }
    } catch (err) {
      console.error('Error fetching cultures:', err);
    }
  };

  // Fetch cultures for editing (only manageable ones)
  const fetchManageableCultures = async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await apiGet('/api/students/cultures/manageable', token);

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched manageable cultures:', data.cultures);
        setCultures(data.cultures || []);
      } else {
        console.error('Failed to fetch manageable cultures:', response.status);
      }
    } catch (err) {
      console.error('Error fetching manageable cultures:', err);
    }
  };

  // Fetch cultures for editing a specific student (includes student's existing cultures)
  const fetchManageableCulturesForStudent = async (studentId: number) => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await apiGet(`/api/students/cultures/manageable?student_id=${studentId}`, token);

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched manageable cultures for student:', data.cultures);
        setCultures(data.cultures || []);
      } else {
        console.error('Failed to fetch manageable cultures:', response.status);
      }
    } catch (err) {
      console.error('Error fetching manageable cultures for student:', err);
    }
  };


  // Search for students by name in real-time (for add form)
  const searchStudentsByName = async (firstName: string, lastName: string) => {
    if (!token || !isAuthenticated) return;
    
    // Need at least 2 characters in either field
    if (firstName.length < 2 && lastName.length < 2) {
      setNameSearchResults([]);
      setShowNameMatches(false);
      return;
    }
    
    setIsSearchingNames(true);
    try {
      const params = new URLSearchParams();
      if (firstName) params.append('first_name', firstName);
      if (lastName) params.append('last_name', lastName);
      params.append('limit', '5');
      
      const response = await apiGet(`/api/students/search-by-name?${params}`, token);
      
      if (response.ok) {
        const data = await response.json();
        setNameSearchResults(data.students || []);
        setShowNameMatches((data.students || []).length > 0);
      }
    } catch (err) {
      console.error('Error searching students by name:', err);
    } finally {
      setIsSearchingNames(false);
    }
  };

  // Fetch teacher's classes for add-to-roster modal
  const fetchTeacherClasses = async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await apiGet('/api/classes?include_students=true', token);
      if (response.ok) {
        const data = await response.json();
        setTeacherClasses(data.classes || []);
      }
    } catch (err) {
      console.error('Error fetching teacher classes:', err);
    }
  };

  // Add student to roster (enroll in selected classes)
  const handleAddToRoster = async () => {
    if (!studentToAddToRoster || !token || selectedClassesForRoster.length === 0) return;
    
    setIsAddingToRoster(true);
    try {
      const response = await apiPost(
        `/api/students/${studentToAddToRoster.id}/add-to-roster`,
        { class_ids: selectedClassesForRoster },
        token
      );
      
      const result = await response.json();
      
      if (response.ok) {
        setIsAddToRosterModalOpen(false);
        setStudentToAddToRoster(null);
        setSelectedClassesForRoster([]);
        
        // Refresh students list
        fetchStudents(false);
        
        setNotificationModal({
          isOpen: true,
          title: t('students.addToRoster.success'),
          message: t('students.addToRoster.successMessage', {
            name: studentToAddToRoster.full_name,
            count: result.enrolled_in?.length || selectedClassesForRoster.length
          }),
          type: 'success'
        });
      } else {
        setNotificationModal({
          isOpen: true,
          title: t('common.error'),
          message: result.error || t('students.addToRoster.failed'),
          type: 'error'
        });
      }
    } catch (err) {
      console.error('Error adding student to roster:', err);
      setNotificationModal({
        isOpen: true,
        title: t('common.error'),
        message: t('students.addToRoster.failed'),
        type: 'error'
      });
    } finally {
      setIsAddingToRoster(false);
    }
  };

  // Open add-to-roster modal
  const openAddToRosterModal = (student: Student) => {
    setStudentToAddToRoster(student);
    setSelectedClassesForRoster([]);
    fetchTeacherClasses();
    setIsAddToRosterModalOpen(true);
  };

  // Handle using an existing student from name search
  const handleUseExistingStudent = (student: Student & { in_my_roster?: boolean }) => {
    if (student.in_my_roster) {
      // Already in roster - just close modal and show message
      handleCloseAddModal();
      setNotificationModal({
        isOpen: true,
        title: t('students.alreadyInRoster'),
        message: t('students.alreadyInRosterMessage', { name: student.full_name }),
        type: 'info'
      });
    } else {
      // Open add-to-roster modal
      handleCloseAddModal();
      openAddToRosterModal(student);
    }
  };

  // Load initial data on mount
  useEffect(() => {
    if (!authLoading && isAuthenticated && token) {
      fetchCultures();
      fetchStudents(true); // Initial load
    } else if (!authLoading) {
      setLoading(false); // Clear loading if no auth
    }
  }, [authLoading, isAuthenticated, token, user?.user_role]);

  // Debounced name search effect for add form
  useEffect(() => {
    if (!isAddModalOpen || isEditModalOpen) {
      setNameSearchResults([]);
      setShowNameMatches(false);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      searchStudentsByName(formData.first_name, formData.last_name);
    }, 400); // 400ms delay for name search
    
    return () => clearTimeout(timeoutId);
  }, [formData.first_name, formData.last_name, isAddModalOpen, isEditModalOpen]);

  // Debounced search effect
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const timeoutId = setTimeout(() => {
      fetchStudents(false); // Not initial load
    }, 300); // 300ms delay for search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Immediate fetch for sort/page/status/viewType changes
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    fetchStudents(false); // Not initial load
  }, [sortBy, sortOrder, page, statusFilter, viewType]);

  // Refetch when organization/institution selection changes
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    fetchStudents(false); // Not initial load
  }, [selectedOrganizationId, selectedInstitutionId]);

  // Sorting and interaction handlers
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('students.details.notProvided');
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return t('students.details.invalidDate');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <SortIcon className="sort-icon inactive" />;
    return <SortIcon className={`sort-icon ${sortOrder === 'desc' ? 'desc' : 'asc'}`} />;
  };

  // Form handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  };

  const handleCultureToggle = (cultureId: number) => {
    // Find the culture to check if it's manageable
    const culture = cultures.find(c => c.id === cultureId);
    
    // Only prevent toggling if explicitly marked as not manageable
    if (culture && culture.manageable === false) {
      console.log('Preventing toggle for unmanageable culture:', culture.name);
      return;
    }
    
    console.log('Toggling culture:', culture?.name, 'manageable:', culture?.manageable);
    
    setFormData(prev => ({
      ...prev,
      culture_ids: prev.culture_ids.includes(cultureId)
        ? prev.culture_ids.filter(id => id !== cultureId)
        : [...prev.culture_ids, cultureId]
    }));
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.first_name.trim()) {
      errors.push(t('students.errors.firstNameRequired'));
    }
    if (!formData.last_name.trim()) {
      errors.push(t('students.errors.lastNameRequired'));
    }
    if (formData.reading_level && (isNaN(Number(formData.reading_level)) || Number(formData.reading_level) < 0)) {
      errors.push(t('students.errors.invalidReadingLevel'));
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(t('students.errors.invalidEmail'));
    }
    if (formData.parent_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parent_email)) {
      errors.push(t('students.errors.invalidParentEmail'));
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (editingStudent) {
      await handleUpdateStudent();
    } else {
      await handleCreateStudent();
    }
  };

  const handleCreateStudent = async () => {
    setFormSubmitting(true);
    setFormErrors([]);

    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        reading_level: formData.reading_level ? parseInt(formData.reading_level) : null,
        grade_level: formData.grade_level || null,
        student_id: formData.student_id || null,
        email: formData.email || null,
        parent_email: formData.parent_email || null,
        phone: formData.phone || null,
        primary_language: formData.primary_language || null,
        notes: formData.notes || null,
        culture_ids: formData.culture_ids.length > 0 ? formData.culture_ids : []
      };

      const response = await apiPost('/api/students', submissionData, token);

      const result = await response.json();

      if (response.ok) {
        // Success - add student to list and close modal
        setStudents(prev => [result.student, ...prev]);
        setTotalStudentsInDatabase(prev => prev + 1); // Update total count
        handleCloseAddModal();
        
        // Refresh the view to show the new student
        fetchStudents(false);
      } else if (response.status === 409 && result.action === 'duplicate_found') {
        // Handle duplicate detection
        setDuplicateMatches(result.potential_matches || []);
        setPendingStudentData(result.new_student_data);
        setIsDuplicateModalOpen(true);
        handleCloseAddModal(); // Close the add modal
      } else {
        // Handle other errors
        if (result.error) {
          setFormErrors([result.error]);
        } else {
          setFormErrors([t('students.errors.createFailed')]);
        }
      }
    } catch (error) {
      setFormErrors([t('students.errors.networkError')]);
      console.error('Error creating student:', error);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleUpdateStudent = async () => {
    if (!editingStudent) return;
    
    setFormSubmitting(true);
    setFormErrors([]);

    try {
      // Prepare submission data
      const submissionData: any = {
        ...formData,
        reading_level: formData.reading_level ? parseInt(formData.reading_level) : null,
        grade_level: formData.grade_level || null,
        student_id: formData.student_id || null,
        email: formData.email || null,
        parent_email: formData.parent_email || null,
        phone: formData.phone || null,
        primary_language: formData.primary_language || null,
        notes: formData.notes || null,
        culture_ids: formData.culture_ids.length > 0 ? formData.culture_ids : []
      };
      
      // Only include password if it's been changed (not empty)
      if (formData.password && formData.password.trim()) {
        submissionData.password = formData.password.trim();
      }

      const response = await apiPut(`/api/students/${editingStudent.id}`, submissionData, token);

      const result = await response.json();

      if (response.ok) {
        // Success - update student in list and close modal
        setStudents(prev => 
          prev.map(s => s.id === editingStudent.id ? result.student : s)
        );
        handleCloseEditModal();
        
        // Refresh the view to show updated data
        fetchStudents(false);
      } else {
        // Handle errors
        if (result.error) {
          setFormErrors([result.error]);
        } else {
          setFormErrors([t('students.errors.updateFailed')]);
        }
      }
    } catch (error) {
      setFormErrors([t('students.errors.networkError')]);
      console.error('Error updating student:', error);
    } finally {
      setFormSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      reading_level: '',
      grade_level: '',
      student_id: '',
      email: '',
      parent_email: '',
      phone: '',
      password: '',
      primary_language: '',
      culture_ids: [],
      notes: ''
    });
    setFormErrors([]);
  };

  const handleOpenAddModal = () => {
    console.log('Opening add modal');
    resetForm();
    setIsAddModalOpen(true);
    
    // Fetch manageable cultures for adding (only cultures user can manage)
    fetchManageableCultures();
  };

  const handleCloseAddModal = () => {
    console.log('Closing add modal');
    setIsAddModalOpen(false);
    resetForm();
    
    // Restore full culture list for display
    fetchCultures();
  };

  const handleDuplicateResolution = async (action: 'use_existing' | 'create_new', studentId?: number, enrolledClasses?: number[]) => {
    try {
      if (!pendingStudentData) {
        console.error('No pending student data available for duplicate resolution');
        return;
      }

      if (action === 'use_existing' && studentId) {
        // Use existing student - close modal and refresh the list
        setIsDuplicateModalOpen(false);
        setDuplicateMatches([]);
        setPendingStudentData(null);
        fetchStudents(false);
        
        // Show success message based on whether classes were selected
        if (enrolledClasses && enrolledClasses.length > 0) {
          setNotificationModal({
            isOpen: true,
            title: t('duplicateResolution.studentAdded'),
            message: t('duplicateResolution.studentEnrolledSuccess', { count: enrolledClasses.length }),
            type: 'success'
          });
        } else {
          setNotificationModal({
            isOpen: true,
            title: t('duplicateResolution.studentFound'),
            message: t('duplicateResolution.usingExistingRecord'),
            type: 'info'
          });
        }
      } else if (action === 'create_new') {
        // Create new student with force_create flag
        const response = await apiPost('/api/students/duplicate-resolution', {
          action: 'create_new',
          new_student_data: {
            ...pendingStudentData,
            force_create: true
          }
        }, token);

        const result = await response.json();

        if (response.ok) {
          setIsDuplicateModalOpen(false);
          setDuplicateMatches([]);
          setPendingStudentData(null);
          
          // Add new student to list
          setStudents(prev => [result.student, ...prev]);
          setTotalStudentsInDatabase(prev => prev + 1);
          fetchStudents(false);
          
          setNotificationModal({
            isOpen: true,
            title: t('students.notifications.studentCreatedTitle'),
            message: t('students.notifications.studentCreatedMessage'),
            type: 'success'
          });
        } else {
          setNotificationModal({
            isOpen: true,
            title: t('students.notifications.creationFailedTitle'),
            message: t('students.notifications.creationFailedMessage', { reason: result.error || t('common.unknownError') }),
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error resolving duplicate:', error);
      setNotificationModal({
        isOpen: true,
        title: t('common.error'),
        message: t('students.notifications.resolveDuplicateFailedMessage'),
        type: 'error'
      });
    }
  };

  const handleOpenEditModal = (student: Student) => {
    console.log('Opening edit modal for student:', student.full_name);
    setEditingStudent(student);
    
    // Pre-populate form with student data
    setFormData({
      first_name: student.first_name,
      last_name: student.last_name,
      reading_level: student.reading_level ? student.reading_level.toString() : '',
      grade_level: student.grade_level || '',
      student_id: student.student_id || '',
      email: student.email || '',
      parent_email: '', // We don't store parent_email in display data, will load from API
      phone: '', // We don't store phone in display data, will load from API  
      password: '', // Password field is always empty for security
      primary_language: student.primary_language || '', // Will be loaded from API
      culture_ids: [], // Will be populated from student.cultures
      notes: '' // Will be loaded from API
    });
    
    setFormErrors([]);
    setIsEditModalOpen(true);
    
    // Fetch manageable cultures for editing (passing student ID to get relevant cultures)
    fetchManageableCulturesForStudent(student.id);
    
    // Fetch full student details including cultures
    fetchStudentDetails(student.id);
  };

  const handleCloseEditModal = () => {
    console.log('Closing edit modal');
    setIsEditModalOpen(false);
    setEditingStudent(null);
    resetForm();
    
    // Restore full culture list for display
    fetchCultures();
  };

  const handleOpenDeleteModal = (student: Student) => {
    console.log('Opening delete confirmation for student:', student.full_name);
    setDeletingStudent(student);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    console.log('Closing delete modal');
    setIsDeleteModalOpen(false);
    setDeletingStudent(null);
  };

  const handleDeleteStudent = async () => {
    if (!deletingStudent) return;

    setIsDeleting(true);

    try {
      const response = await apiDelete(`/api/students/${deletingStudent.id}`, token);

      const result = await response.json();

      if (response.ok) {
        // Remove student from list
        setStudents(prev => prev.filter(s => s.id !== deletingStudent.id));
        setTotalStudentsInDatabase(prev => prev - 1);
        handleCloseDeleteModal();
        
        // Refresh the view
        fetchStudents(false);
      } else {
        setNotificationModal({
          isOpen: true,
          title: t('students.notifications.deleteFailedTitle'),
          message: t('students.notifications.deleteFailedMessage', { reason: result.error || t('common.unknownError') }),
          type: 'error'
        });
      }
    } catch (error) {
      setNotificationModal({
        isOpen: true,
        title: t('students.notifications.networkErrorTitle'),
        message: t('students.errors.networkError'),
        type: 'error'
      });
      console.error('Error deleting student:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Reactivate student function
  const handleReactivateStudent = async (student: Student) => {
    try {
      const response = await apiPost(`/api/students/${student.id}/restore`, undefined, token);

      const result = await response.json();

      if (response.ok) {
        setNotificationModal({
          isOpen: true,
          title: t('students.notifications.studentReactivatedTitle'),
          message: t('students.notifications.studentReactivatedMessage', { name: student.full_name }),
          type: 'success'
        });
        
        // Refresh the view
        fetchStudents(false);
      } else {
        setNotificationModal({
          isOpen: true,
          title: t('students.notifications.reactivationFailedTitle'),
          message: t('students.notifications.reactivationFailedMessage', { reason: result.error || t('common.unknownError') }),
          type: 'error'
        });
      }
    } catch (error) {
      setNotificationModal({
        isOpen: true,
        title: t('students.notifications.networkErrorTitle'),
        message: t('students.errors.networkError'),
        type: 'error'
      });
      console.error('Error reactivating student:', error);
    }
  };

  // Permanent delete modal handlers
  const handleOpenPermanentDeleteModal = (student: Student) => {
    setPermanentDeletingStudent(student);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleClosePermanentDeleteModal = () => {
    setIsPermanentDeleteModalOpen(false);
    setPermanentDeletingStudent(null);
  };

  // Permanent delete student function  
  const handlePermanentlyDeleteStudent = async () => {
    if (!permanentDeletingStudent) return;

    setIsPermanentDeleting(true);

    try {
      const response = await apiDelete(`/api/students/${permanentDeletingStudent.id}/permanent`, token);

      const result = await response.json();

      if (response.ok) {
        handleClosePermanentDeleteModal();
        setNotificationModal({
          isOpen: true,
          title: t('students.notifications.studentPermanentlyDeletedTitle'),
          message: t('students.notifications.studentPermanentlyDeletedMessage', { name: permanentDeletingStudent.full_name }),
          type: 'success'
        });
        
        // Refresh the view
        fetchStudents(false);
      } else {
        setNotificationModal({
          isOpen: true,
          title: t('students.notifications.permanentDeleteFailedTitle'),
          message: t('students.notifications.permanentDeleteFailedMessage', { reason: result.error || t('common.unknownError') }),
          type: 'error'
        });
      }
    } catch (error) {
      setNotificationModal({
        isOpen: true,
        title: t('students.notifications.networkErrorTitle'),
        message: t('students.errors.networkError'),
        type: 'error'
      });
      console.error('Error permanently deleting student:', error);
    } finally {
      setIsPermanentDeleting(false);
    }
  };

  // Handle row click to navigate to student detail
  const handleRowClick = (student: Student, e: React.MouseEvent) => {
    // Don't navigate if user clicked on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('.action-btn') || target.closest('button')) {
      return;
    }
    
    navigate(`/students/${student.id}`);
  };

  // Wizard handlers
  const handleOpenWizard = (student: Student) => {
    setWizardStudent(student);
    setIsWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
    setWizardStudent(null);
  };

  // Fetch full student details for editing
  const fetchStudentDetails = async (studentId: number) => {
    try {
      const response = await apiGet(`/api/students/${studentId}`, token);

      if (response.ok) {
        const data = await response.json();
        const student = data.student;
        
        // Update form with complete student data
        setFormData(prev => ({
          ...prev,
          reading_level: student.reading_level ? student.reading_level.toString() : '',
          parent_email: student.parent_email || '',
          phone: student.phone || '',
          primary_language: student.primary_language || '',
          notes: student.notes || '',
          culture_ids: student.culture_codes ? 
            cultures.filter(c => student.culture_codes.includes(c.code)).map(c => c.id) : []
        }));
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
    }
  };

  // Authentication check
  if (authLoading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>{t('students.checkingAuth')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="error-state">
              <p>{t('students.loginRequired')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state (only for data loading, not auth)
  if (loading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>{t('students.loadingStudents')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="error-state">
              <p>{t('common.error')}: {error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="retry-btn"
                aria-label={t('students.aria.retryLoadStudents')}
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Students list view
  return (
    <div className="dashboard">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <div className="students-section">
            <div className="students-header">
              <div className="page-header-content">
                <h1>{t('students.title')}</h1>
                <p>{stats.total_students === 1 ? t('students.manageCount', { count: stats.total_students }) : t('students.manageCount_plural', { count: stats.total_students })}</p>
              </div>
              <div className="header-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setIsGoogleClassroomImportOpen(true)}
                  aria-label={t('students.aria.importFromGoogleAria')}
                >
                  <CloudDownloadIcon aria-hidden="true" />
                  {t('students.importFromGoogle')}
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleOpenAddModal}
                  aria-label={t('students.aria.addNewStudentAria')}
                >
                  <PersonAddIcon aria-hidden="true" />
                  {t('students.addStudent')}
                </button>
              </div>
            </div>

            {/* View Type Tabs */}
            <div className="view-tabs" role="tablist" aria-label={t('students.view.label')}>
              <button
                className={`view-tab ${viewType === 'my_classes' ? 'active' : ''}`}
                onClick={() => setViewType('my_classes')}
                aria-label={t('students.view.myClassesOnlyAria')}
                aria-selected={viewType === 'my_classes'}
                role="tab"
              >
                {t('students.view.myRoster')}
              </button>
              <button
                className={`view-tab ${viewType === 'all' ? 'active' : ''}`}
                onClick={() => setViewType('all')}
                aria-label={t('students.view.allStudentsAria')}
                aria-selected={viewType === 'all'}
                role="tab"
              >
                {t('students.view.allStudents')}
              </button>
              <button
                className={`view-tab ${viewType === 'institution' ? 'active' : ''}`}
                onClick={() => setViewType('institution')}
                aria-label={t('students.view.institutionAria')}
                aria-selected={viewType === 'institution'}
                role="tab"
              >
                {t('students.view.institution')}
              </button>
            </div>

            {/* Compact Stats Strip */}
            <div className="stats-strip">
              <div className="stat-pill">
                <span className="stat-value">{stats.total_students}</span>
                <span className="stat-label">{t('students.stats.totalStudents')}</span>
              </div>
              <div className="stat-pill">
                <span className="stat-value">0</span>
                <span className="stat-label">{t('students.stats.addedThisMonth')}</span>
              </div>
              <div className="stat-pill stat-active">
                <span className="stat-dot" aria-hidden="true"></span>
                <span className="stat-value">{students.filter(s => s.is_active).length} {t('students.stats.active')}</span>
              </div>
            </div>

            {viewType === 'institution' && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '10px 14px', 
                background: '#f0fdf4', 
                borderRadius: '6px',
                fontSize: '13px',
                color: '#166534',
                border: '1px solid #a7f3d0'
              }}>
                💡 {t('students.view.institutionHint')}
              </div>
            )}

            {/* Search and Filter Controls */}
            <div className="controls-bar">
              <div className="search-container">
                <SearchIcon className="search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder={t('students.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="search-input"
                  disabled={dataLoading}
                  aria-label={t('students.searchPlaceholder')}
                />
                {dataLoading && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '14px',
                    height: '14px',
                    border: '2px solid #e5e7eb',
                    borderTop: '2px solid #059669',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} aria-label="Searching..." />
                )}
                {!dataLoading && searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1
                    }}
                    aria-label={t('students.aria.clearSearchAria')}
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="filter-controls">
                {/* Sort By */}
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  className="filter-select"
                  disabled={dataLoading}
                  aria-label={t('students.filters.sortBy')}
                >
                  <option value="last_name">{t('students.filters.nameAZ')}</option>
                  <option value="first_name">{t('students.filters.firstName')}</option>
                  <option value="grade_level">{t('students.filters.grade')}</option>
                  <option value="reading_level">{t('students.filters.readingLevel')}</option>
                </select>
                
                {/* Status Filter */}
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                    setPage(1);
                  }}
                  className="filter-select"
                  disabled={dataLoading}
                  aria-label={t('students.filters.status')}
                >
                  <option value="active">{t('students.filters.active')}</option>
                  <option value="inactive">{t('students.filters.inactive')}</option>
                  <option value="all">{t('students.filters.allStatus')}</option>
                </select>
              </div>
            </div>

            {/* Students Table */}
            <div className="students-table-container">
              <table className="students-table" role="grid" aria-label={t('students.title')}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('last_name')} className="sortable" style={{ minWidth: '200px' }}>
                      {t('students.table.student')} {getSortIcon('last_name')}
                    </th>
                    <th onClick={() => handleSort('grade_level')} className="sortable">
                      {t('students.table.gradeLevel')} {getSortIcon('grade_level')}
                    </th>
                    <th onClick={() => handleSort('reading_level')} className="sortable">
                      {t('students.table.readingLevel')} {getSortIcon('reading_level')}
                    </th>
                    <th onClick={() => handleSort('primary_language')} className="sortable">
                      {t('students.table.primaryLanguage')} {getSortIcon('primary_language')}
                    </th>
                    <th>{t('students.table.attributes')}</th>
                    <th>{t('students.table.classes')}</th>
                    <th onClick={() => handleSort('assignment_count')} className="sortable" style={{ textAlign: 'right' }}>
                      {t('students.table.assignments')} {getSortIcon('assignment_count')}
                    </th>
                    <th style={{ width: '48px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {students.length > 0 ? (
                    students.map((student) => (
                      <tr 
                        key={student.id} 
                        className={`student-row clickable-row ${!student.is_active ? 'inactive-student' : ''}`}
                        onClick={(e) => handleRowClick(student, e)}
                        title={t('students.clickToView')}
                      >
                        <td data-label={t('students.table.student')}>
                          <div className="student-name">
                            <div className="student-avatar" aria-hidden="true">
                              {student.first_name.charAt(0)}{student.last_name.charAt(0)}
                            </div>
                            <div>
                              <span className="name">{student.first_name} {student.last_name}</span>
                              {student.email && (
                                <div className="email">{student.email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td data-label={t('students.table.gradeLevel')}>
                          <span className={`grade-level ${student.grade_level ? 'has-grade' : 'no-grade'}`}>
                            {student.grade_level || t('students.details.notAssigned')}
                          </span>
                        </td>
                        <td data-label={t('students.table.readingLevel')}>
                          <span className={`reading-level ${student.reading_level ? 'has-level' : 'no-level'}`}>
                            {student.reading_level ? t('students.details.level', { level: student.reading_level }) : t('students.details.notAssessed')}
                          </span>
                        </td>
                        <td data-label={t('students.table.primaryLanguage')}>
                          <span className={`primary-language ${student.primary_language ? 'has-language' : 'no-language'}`}>
                            {student.primary_language 
                              ? t(`students.form.languages.${student.primary_language}`, student.primary_language)
                              : t('students.details.notAssigned')}
                          </span>
                        </td>
                        <td data-label={t('students.table.attributes')}>
                          <div className="cultures-cell">
                            {student.cultures.length > 0 ? (
                              <div className="culture-tags">
                                {student.cultures.slice(0, 2).map((cultureName, index) => {
                                  const cultureObj = cultures.find(c => c.name === cultureName);
                                  const isManageable = cultureObj?.manageable !== false;
                                  
                                  return (
                                    <span 
                                      key={index} 
                                      className="culture-tag"
                                      style={{
                                        opacity: isManageable ? 1 : 0.7
                                      }}
                                      title={!isManageable && cultureObj?.created_by_user_name 
                                        ? `Created by ${cultureObj.created_by_user_name}` 
                                        : cultureName
                                      }
                                    >
                                      {cultureName}
                                    </span>
                                  );
                                })}
                                {student.cultures.length > 2 && (
                                  <span className="culture-more">
                                    {t('students.details.moreItems', { count: student.cultures.length - 2 })}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="no-cultures">{t('students.details.noneAssigned')}</span>
                            )}
                          </div>
                        </td>
                        <td data-label={t('students.table.classes')}>
                          <div className="classes-cell">
                            {student.class_count > 0 ? (
                              <span className="class-count">
                                {student.class_count} {student.class_count === 1 ? t('students.table.classSingular') : t('students.table.classPlural')}
                              </span>
                            ) : (
                              <span className="no-classes">{t('students.details.notEnrolled')}</span>
                            )}
                          </div>
                        </td>
                        <td data-label={t('students.table.assignments')} style={{ textAlign: 'right', fontWeight: 500 }}>
                          {student.assignment_count || 0}
                        </td>
                        <td data-label={t('students.table.actions')}>
                          <div className="actions-cell">
                            {viewType === 'institution' ? (
                              // Institution view - show Add to Roster or In Roster indicator
                              student.in_my_roster ? (
                                <span style={{ 
                                  color: '#059669', 
                                  fontWeight: '600', 
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  ✓ {t('students.inRoster')}
                                </span>
                              ) : (
                                <button 
                                  className="action-btn add-to-roster-btn" 
                                  aria-label={t('students.addToRosterAria', { name: student.full_name })}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAddToRosterModal(student);
                                  }}
                                  style={{ 
                                    backgroundColor: '#2d5016', 
                                    borderColor: '#2d5016',
                                    padding: '6px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#fff',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <PersonAddIcon style={{ fontSize: '16px' }} aria-hidden="true" />
                                  {t('students.addToRosterBtn')}
                                </button>
                              )
                            ) : student.is_active ? (
                              // Active student buttons (normal view)
                              <>
                                <button 
                                  className="action-btn wizard-btn" 
                                  aria-label={t('students.aria.onboardStudentAria', { name: `${student.first_name} ${student.last_name}` })}
                                  onClick={(e) => { e.stopPropagation(); handleOpenWizard(student); }}
                                >
                                  <WizardIcon aria-hidden="true" />
                                </button>
                                <button 
                                  className="action-btn edit-btn" 
                                  aria-label={t('students.aria.editStudentAria', { name: `${student.first_name} ${student.last_name}` })}
                                  onClick={(e) => { e.stopPropagation(); handleOpenEditModal(student); }}
                                >
                                  <EditIcon aria-hidden="true" />
                                </button>
                                <button 
                                  className="action-btn delete-btn" 
                                  aria-label={t('students.aria.deleteStudentAria', { name: `${student.first_name} ${student.last_name}` })}
                                  onClick={(e) => { e.stopPropagation(); handleOpenDeleteModal(student); }}
                                >
                                  <DeleteIcon aria-hidden="true" />
                                </button>
                              </>
                            ) : (
                              // Inactive student buttons
                              <>
                                <button 
                                  className="action-btn reactivate-btn" 
                                  aria-label={t('students.aria.reactivateStudentAria', { name: `${student.first_name} ${student.last_name}` })}
                                  onClick={(e) => { e.stopPropagation(); handleReactivateStudent(student); }}
                                  style={{ 
                                    backgroundColor: '#16a34a', 
                                    borderColor: '#16a34a' 
                                  }}
                                >
                                  <RestoreIcon aria-hidden="true" />
                                </button>
                                <button 
                                  className="action-btn permanent-delete-btn" 
                                  aria-label={t('students.aria.permanentlyDeleteStudentAria', { name: `${student.first_name} ${student.last_name}` })}
                                  onClick={(e) => { e.stopPropagation(); handleOpenPermanentDeleteModal(student); }}
                                  style={{ 
                                    backgroundColor: '#dc2626', 
                                    borderColor: '#dc2626' 
                                  }}
                                >
                                  <DeleteForeverIcon aria-hidden="true" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#6b7280',
                        fontStyle: 'italic'
                      }}>
                        {dataLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid #e8e2db',
                              borderTop: '2px solid #d4a574',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }} />
                        {t('students.searching')}
                          </div>
                        ) : searchTerm ? (
                          <div>
                            <p style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
                          {t('students.noStudentsMatching', { searchTerm })}
                            </p>
                            <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af' }}>
                          {t('students.tryAdjusting')}
                            </p>
                          </div>
                        ) : (
                      t('students.noStudentsFound')
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn" 
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  aria-label={t('students.aria.previousPageAria')}
                >
                  {t('students.pagination.previous')}
                </button>
                
                <span className="pagination-info" aria-live="polite" aria-atomic="true">
                  {t('students.pagination.pageOf', { current: page, total: totalPages })}
                </span>
                
                <button 
                  className="pagination-btn"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  aria-label={t('students.aria.nextPageAria')}
                >
                  {t('students.pagination.next')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Student Modal */}
        {(isAddModalOpen || isEditModalOpen) && (
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: true,
              escapeDeactivates: true
            }}
          >
            <div style={{
              position: 'fixed',
              top: '0',
              left: '0',
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px'
            }} role="presentation">
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="student-modal-title"
              >
                {/* Modal Header */}
                <div style={{
                  padding: '24px 32px',
                  borderBottom: '2px solid #e8e2db',
                  background: 'linear-gradient(135deg, #f7f5f0 0%, #e8e2db 100%)'
                }}>
                  <h2 id="student-modal-title" style={{ 
                    margin: 0, 
                    color: '#2d5016', 
                    fontSize: '24px', 
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <PersonIcon style={{ fontSize: '28px' }} aria-hidden="true" />
                    {editingStudent ? t('students.form.editStudent', { name: editingStudent.full_name }) : t('students.form.addNew')}
                  </h2>
                </div>

              {/* Form Content */}
              <form onSubmit={handleFormSubmit} style={{ padding: '32px' }}>
                {/* Error Messages */}
                {formErrors.length > 0 && (
                  <div style={{
                    background: '#fee',
                    border: '2px solid #fca5a5',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '24px'
                  }}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={{
                        color: '#dc2626',
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: index < formErrors.length - 1 ? '4px' : '0'
                      }}>
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                {/* Basic Information */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2d5016',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e8e2db'
                  }}>
                    Basic Information
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        First Name <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        Last Name <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  {/* Real-time Name Match Results */}
                  {!editingStudent && (showNameMatches || isSearchingNames) && (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      background: '#f0fdf4',
                      border: '2px solid #86efac',
                      borderRadius: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginBottom: '12px',
                        color: '#166534',
                        fontWeight: 'bold'
                      }}>
                        <SearchIcon style={{ fontSize: '20px' }} aria-hidden="true" />
                        {isSearchingNames ? (
                          t('students.nameSearch.searching')
                        ) : (
                          t('students.nameSearch.matchesFound')
                        )}
                      </div>
                      
                      {nameSearchResults.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {nameSearchResults.map((student) => (
                            <div 
                              key={student.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px',
                                background: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db'
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 'bold', color: '#1f2937' }}>
                                  {student.full_name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {[student.grade_level, student.email, student.student_id].filter(Boolean).join(' • ')}
                                  {student.in_my_roster && (
                                    <span style={{ 
                                      marginLeft: '8px', 
                                      color: '#059669', 
                                      fontWeight: '600' 
                                    }}>
                                      ✓ {t('students.nameSearch.inYourRoster')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUseExistingStudent(student)}
                                style={{
                                  padding: '8px 16px',
                                  background: student.in_my_roster ? '#e5e7eb' : '#2d5016',
                                  color: student.in_my_roster ? '#374151' : '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  fontSize: '13px'
                                }}
                                aria-label={student.in_my_roster 
                                  ? t('students.nameSearch.alreadyInRoster')
                                  : t('students.nameSearch.addToRoster')}
                              >
                                {student.in_my_roster 
                                  ? t('students.nameSearch.viewStudent')
                                  : t('students.nameSearch.addToRoster')}
                              </button>
                            </div>
                          ))}
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280', 
                            marginTop: '8px',
                            fontStyle: 'italic'
                          }}>
                            {t('students.nameSearch.hint')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '20px' }}>
                    <div style={{ maxWidth: '50%' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        {t('students.form.studentId')}
                      </label>
                      <input
                        type="text"
                        name="student_id"
                        aria-label={t('students.form.studentId')}
                        value={formData.student_id}
                        onChange={handleInputChange}
                        placeholder={t('students.form.studentIdPlaceholder')}
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Academic Information */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2d5016',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e8e2db'
                  }}>
                    Academic Information
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        Grade Level
                      </label>
                      <select
                        name="grade_level"
                        value={formData.grade_level}
                        onChange={handleInputChange}
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">{t('students.form.selectGrade')}</option>
                        <option value="K">{t('students.form.grades.K')}</option>
                        <option value="1st">{t('students.form.grades.1st')}</option>
                        <option value="2nd">{t('students.form.grades.2nd')}</option>
                        <option value="3rd">{t('students.form.grades.3rd')}</option>
                        <option value="4th">{t('students.form.grades.4th')}</option>
                        <option value="5th">{t('students.form.grades.5th')}</option>
                        <option value="6th">{t('students.form.grades.6th')}</option>
                        <option value="7th">{t('students.form.grades.7th')}</option>
                        <option value="8th">{t('students.form.grades.8th')}</option>
                        <option value="9th">{t('students.form.grades.9th')}</option>
                        <option value="10th">{t('students.form.grades.10th')}</option>
                        <option value="11th">{t('students.form.grades.11th')}</option>
                        <option value="12th">{t('students.form.grades.12th')}</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        Reading Level
                      </label>
                      <input
                        type="number"
                        name="reading_level"
                        value={formData.reading_level}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="e.g. 300, 450"
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2d5016',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e8e2db'
                  }}>
                    Contact Information
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        Student Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="student@school.edu"
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        Parent Email
                      </label>
                      <input
                        type="email"
                        name="parent_email"
                        value={formData.parent_email}
                        onChange={handleInputChange}
                        placeholder="parent@example.com"
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (555) 123-4567"
                        disabled={formSubmitting}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Password field - only shown in edit mode */}
                  {isEditModalOpen && editingStudent && (
                    <div style={{ marginTop: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                        {t('students.form.password')}
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={t('students.form.passwordPlaceholder')}
                        disabled={formSubmitting}
                        aria-label={t('students.form.password')}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e8e2db',
                          borderRadius: '10px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                      <p style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#6b7280',
                        fontStyle: 'italic'
                      }}>
                        {t('students.form.passwordChangeNote')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Primary Language */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2d5016',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e8e2db'
                  }}>
                    {t('students.form.primaryLanguage')}
                  </h3>
                  
                  <div style={{ maxWidth: '50%' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                      {t('students.form.selectPrimaryLanguage')}
                    </label>
                    <select
                      name="primary_language"
                      aria-label={t('students.form.primaryLanguage')}
                      value={formData.primary_language}
                      onChange={handleInputChange}
                      disabled={formSubmitting}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e8e2db',
                        borderRadius: '10px',
                        fontSize: '16px',
                        outline: 'none',
                        cursor: 'pointer',
                        minWidth: '200px'
                      }}
                    >
                      <option value="">{t('students.form.selectLanguageOption')}</option>
                      <option value="en">{t('students.form.languages.en')}</option>
                      <option value="es">{t('students.form.languages.es')}</option>
                      <option value="fr">{t('students.form.languages.fr')}</option>
                      <option value="de">{t('students.form.languages.de')}</option>
                      <option value="zh">{t('students.form.languages.zh')}</option>
                      <option value="vi">{t('students.form.languages.vi')}</option>
                      <option value="ar">{t('students.form.languages.ar')}</option>
                      <option value="ko">{t('students.form.languages.ko')}</option>
                      <option value="tl">{t('students.form.languages.tl')}</option>
                      <option value="pt">{t('students.form.languages.pt')}</option>
                      <option value="other">{t('students.form.languages.other')}</option>
                    </select>
                  </div>
                </div>

                {/* Race/Ethnicity */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2d5016',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e8e2db'
                  }}>
                    {t('students.form.raceEthnicity')}
                  </h3>
                  
                  <div style={{
                    background: '#f7f5f0',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e8e2db'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '8px',
                      maxHeight: '150px',
                      overflow: 'auto'
                    }}>
                      {cultures.map((culture) => {
                        // Determine if manageable based on backend flag OR fallback logic
                        let isManageable: boolean;
                        if (culture.manageable !== undefined) {
                          isManageable = culture.manageable;
                        } else {
                          if (culture.culture_type === 'organization') {
                            isManageable = true;
                          } else if (culture.culture_type === 'teacher') {
                            isManageable = culture.created_by_user_id === user?.id;
                          } else {
                            isManageable = true;
                          }
                        }
                        const isChecked = formData.culture_ids.includes(culture.id);
                        
                        return isManageable ? (
                          <label key={culture.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#374151',
                            fontWeight: '500',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            background: isChecked ? '#e8f4f8' : 'transparent'
                          }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleCultureToggle(culture.id)}
                              disabled={formSubmitting}
                              aria-label={culture.name}
                              style={{ accentColor: '#2d5016' }}
                            />
                            {culture.name}
                          </label>
                        ) : (
                          <div key={culture.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'not-allowed',
                            fontSize: '13px',
                            color: '#9ca3af',
                            fontWeight: '500',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            background: '#f3f4f6',
                            opacity: 0.7
                          }}
                          title={culture.created_by_user_name ? `Culture assigned by ${culture.created_by_user_name}, cannot be removed` : undefined}
                          >
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid #d1d5db',
                              borderRadius: '3px',
                              backgroundColor: '#f3f4f6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {isChecked && (
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  backgroundColor: '#9ca3af',
                                  borderRadius: '1px'
                                }} />
                              )}
                            </div>
                            {culture.name}
                            {culture.culture_type === 'teacher' && (
                              <span style={{ fontSize: '10px', fontStyle: 'italic' }}>
                                (by {culture.created_by_user_name})
                              </span>
                            )}
                            <span style={{
                              fontSize: '9px',
                              marginLeft: 'auto',
                              padding: '2px 4px',
                              backgroundColor: '#e5e7eb',
                              borderRadius: '3px'
                            }}>
                              READ-ONLY
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2d5016',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e8e2db'
                  }}>
                    {t('modals.additionalNotes')}
                  </h3>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                      {t('modals.teacherNotes')}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Any additional information about the student..."
                      rows={3}
                      disabled={formSubmitting}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e8e2db',
                        borderRadius: '10px',
                        fontSize: '16px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                </div>

                {/* Submit Buttons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '16px',
                  paddingTop: '24px',
                  borderTop: '2px solid #e8e2db',
                  marginTop: '32px'
                }}>
                  <button
                    type="button"
                    onClick={editingStudent ? handleCloseEditModal : handleCloseAddModal}
                    disabled={formSubmitting}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#ffffff',
                      color: '#2d5016',
                      border: '2px solid #e8e2db',
                      borderRadius: '10px',
                      cursor: formSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      opacity: formSubmitting ? 0.6 : 1,
                      minWidth: '120px'
                    }}
                  >
                    {t('modals.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    style={{
                      padding: '12px 24px',
                      background: formSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #2d5016 0%, #1e3a0f 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: formSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      minWidth: '120px'
                    }}
                  >
                    {formSubmitting ? 
                      (editingStudent ? t('students.form.updating') : t('students.form.creating')) : 
                      (editingStudent ? t('students.form.updateStudent') : t('students.addStudent'))
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
          </FocusTrap>
        )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingStudent && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: true,
            initialFocus: false
          }}
        >
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }} role="presentation">
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              margin: '20px'
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-student-modal-title"
            aria-describedby="delete-student-modal-desc"
            >
              <div style={{
                textAlign: 'center',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'white'
                }} aria-hidden="true">
                  <DeleteIcon style={{ fontSize: '32px' }} />
                </div>
                
                <h2 id="delete-student-modal-title" style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  margin: '0 0 8px 0'
                }}>
                  {t('students.delete.title')}
                </h2>
                
                <p id="delete-student-modal-desc" style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  margin: '0 0 16px 0',
                  lineHeight: '1.5'
                }}>
                  {t('students.delete.confirmation', { name: deletingStudent.full_name })}
                </p>
              
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                margin: '16px 0'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: '#dc2626',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {t('students.delete.warning')}
                </p>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: isDeleting ? 0.6 : 1
                }}
                aria-label={t('common.cancel')}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteStudent}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  background: isDeleting ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  minWidth: '120px'
                }}
              >
                {isDeleting ? t('students.delete.deleting') : t('students.delete.deleteStudent')}
              </button>
            </div>
          </div>
        </div>
        </FocusTrap>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {isPermanentDeleteModalOpen && permanentDeletingStudent && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: true,
            initialFocus: false
          }}
        >
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }} role="presentation">
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '3px solid #dc2626'
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="perm-delete-student-modal-title"
            aria-describedby="perm-delete-student-modal-desc"
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                margin: '0 auto 16px',
                color: 'white'
              }} aria-hidden="true">
                <DeleteForeverIcon style={{ fontSize: '32px' }} />
              </div>
              
              <h2 id="perm-delete-student-modal-title" style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#dc2626',
                textAlign: 'center',
                margin: '0 0 16px 0',
                lineHeight: '1.2'
              }}>
                {t('students.permanentDelete.title')}
              </h2>
              
              <p id="perm-delete-student-modal-desc" style={{
                fontSize: '16px',
                color: '#374151',
                textAlign: 'center',
                margin: '0 0 24px 0',
                lineHeight: '1.5'
              }}>
                {t('students.permanentDelete.messagePrefix')}{' '}
                <strong>{t('students.permanentDelete.deleteVerb')}</strong>{' '}
                <strong style={{ color: '#dc2626' }}>{permanentDeletingStudent.full_name}</strong>{' '}
                {t('students.permanentDelete.messageSuffix')}
              </p>
            
            <div style={{
              background: '#fef2f2',
              border: '2px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              margin: '0 0 24px 0'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#dc2626',
                margin: '0',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                {t('students.permanentDelete.warningTitle')}<br />
                {t('students.permanentDelete.warningBody')}
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleClosePermanentDeleteModal}
                disabled={isPermanentDeleting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: isPermanentDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: isPermanentDeleting ? 0.6 : 1
                }}
                aria-label={t('common.cancel')}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handlePermanentlyDeleteStudent}
                disabled={isPermanentDeleting}
                style={{
                  padding: '12px 24px',
                  background: isPermanentDeleting ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isPermanentDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                aria-label={isPermanentDeleting ? t('students.permanentDelete.deleting') : t('students.permanentDelete.confirmButton')}
              >
                {isPermanentDeleting ? t('students.permanentDelete.deleting') : t('students.permanentDelete.confirmButton')}
              </button>
            </div>
          </div>
        </div>
        </FocusTrap>
      )}

      {/* Google Classroom Import Modal */}
      <GoogleClassroomImport
        isOpen={isGoogleClassroomImportOpen}
        onClose={() => setIsGoogleClassroomImportOpen(false)}
        type="students"
        onImportComplete={() => {
          // Refresh the students list after import
          fetchStudents();
        }}
      />

      {/* Student Onboarding Wizard */}
      <StudentOnboardingWizard
        isOpen={isWizardOpen}
        onClose={handleCloseWizard}
        student={wizardStudent as any}
        onStudentUpdated={(updatedStudent: any) => {
          // Update the student in the list
          setStudents(prev => 
            prev.map(s => s.id === updatedStudent.id ? updatedStudent : s)
          );
          // Refresh the view to show updated data
          fetchStudents(false);
        }}
      />

      {/* Notification Modal */}
      <DuplicateResolutionModal
        open={isDuplicateModalOpen && pendingStudentData !== null}
        onClose={() => {
          setIsDuplicateModalOpen(false);
          setDuplicateMatches([]);
          setPendingStudentData(null);
        }}
        potentialMatches={duplicateMatches}
        newStudentData={pendingStudentData}
        onResolve={handleDuplicateResolution}
        loading={formSubmitting}
      />

      {/* Add to Roster Modal */}
      {isAddToRosterModalOpen && studentToAddToRoster && (
        <FocusTrap>
          <div 
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-to-roster-title"
          >
            <div className="modal-container" style={{ maxWidth: '500px' }}>
              <div className="modal-header" style={{ 
                background: '#2d5016', 
                color: '#fff', 
                padding: '20px 24px',
                borderRadius: '16px 16px 0 0'
              }}>
                <h2 id="add-to-roster-title" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                  <PersonAddIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} aria-hidden="true" />
                  {t('students.addToRoster.title')}
                </h2>
              </div>

              <div style={{ padding: '24px' }}>
                <div style={{ 
                  background: '#f0fdf4', 
                  padding: '16px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: '1px solid #86efac'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#166534', marginBottom: '4px' }}>
                    {studentToAddToRoster.full_name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    {[studentToAddToRoster.grade_level, studentToAddToRoster.email, studentToAddToRoster.student_id]
                      .filter(Boolean).join(' • ') || t('students.addToRoster.noDetails')}
                  </div>
                </div>

                <p style={{ marginBottom: '16px', color: '#374151' }}>
                  {t('students.addToRoster.selectClasses')}
                </p>

                {teacherClasses.length === 0 ? (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: '#6b7280',
                    background: '#f9fafb',
                    borderRadius: '8px'
                  }}>
                    {t('students.addToRoster.noClasses')}
                  </div>
                ) : (
                  <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {teacherClasses.map((cls) => {
                      const isAlreadyEnrolled = studentToAddToRoster.class_ids?.includes(cls.id);
                      return (
                        <label 
                          key={cls.id} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            background: selectedClassesForRoster.includes(cls.id) ? '#f0fdf4' : '#f9fafb',
                            border: selectedClassesForRoster.includes(cls.id) ? '2px solid #2d5016' : '1px solid #e5e7eb',
                            cursor: isAlreadyEnrolled ? 'not-allowed' : 'pointer',
                            opacity: isAlreadyEnrolled ? 0.6 : 1
                          }}
                        >
                          <input 
                            type="checkbox"
                            checked={selectedClassesForRoster.includes(cls.id) || isAlreadyEnrolled}
                            disabled={isAlreadyEnrolled}
                            onChange={() => {
                              if (!isAlreadyEnrolled) {
                                setSelectedClassesForRoster(prev =>
                                  prev.includes(cls.id)
                                    ? prev.filter(id => id !== cls.id)
                                    : [...prev, cls.id]
                                );
                              }
                            }}
                            style={{ marginRight: '12px', width: '18px', height: '18px' }}
                            aria-label={t('students.addToRoster.enrollIn', { className: cls.name })}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#1f2937' }}>{cls.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {cls.subject && `${cls.subject} • `}
                              {cls.student_count ?? 0} {t('students.addToRoster.students')}
                              {isAlreadyEnrolled && (
                                <span style={{ color: '#059669', marginLeft: '8px' }}>
                                  ✓ {t('students.addToRoster.alreadyEnrolled')}
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ 
                padding: '16px 24px', 
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  onClick={() => {
                    setIsAddToRosterModalOpen(false);
                    setStudentToAddToRoster(null);
                    setSelectedClassesForRoster([]);
                  }}
                  disabled={isAddingToRoster}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    background: '#fff',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  aria-label={t('common.cancel')}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleAddToRoster}
                  disabled={isAddingToRoster || selectedClassesForRoster.length === 0}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: selectedClassesForRoster.length === 0 ? '#9ca3af' : '#2d5016',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: selectedClassesForRoster.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  aria-label={t('students.addToRoster.addButton', { count: selectedClassesForRoster.length })}
                >
                  {isAddingToRoster ? (
                    t('common.loading')
                  ) : (
                    <>
                      <PersonAddIcon style={{ fontSize: '18px' }} aria-hidden="true" />
                      {selectedClassesForRoster.length > 0 
                        ? t('students.addToRoster.addButton', { count: selectedClassesForRoster.length })
                        : t('students.addToRoster.selectClassFirst')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
      
      <NotificationModal
        isOpen={notificationModal.isOpen}
        title={notificationModal.title}
        message={notificationModal.message}
        type={notificationModal.type}
        onClose={() => setNotificationModal(prev => ({ ...prev, isOpen: false }))}
      />
      </div>
    </div>
  );
};

export default Students;